import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { Card } from "../../components/UI";
import { Landmark, ArrowRight, Loader2, ShieldCheck, Users2 } from "lucide-react";

export default function SimpleRace() {
  const navigate = useNavigate();
  const [cc, setCc] = useState(null);
  const [ri, setRi] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/political-os/command-center")
      .then(async ({ data }) => {
        setCc(data);
        if (data?.entity?.id) {
          try {
            const r = await api.get(`/political-os/entities/${data.entity.id}/race-intelligence`);
            setRi(r.data);
          } catch { /* race not attached yet */ }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  const entity = cc?.entity;
  const race = cc?.race;
  const candidates = ri?.candidates || [];

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto" data-testid="simple-race">
      <div className="text-[11px] uppercase tracking-widest text-cyan-300 font-semibold mb-2">Race</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold mb-6">Your race brief</h1>

      {!entity ? (
        <Card>
          <p className="text-sm text-slate-400 mb-4">Set up your political entity to see your race brief.</p>
          <button onClick={() => navigate("/onboarding/political")} data-testid="race-onboard-btn"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold rounded-full px-5 py-2.5 text-sm hover:opacity-90">
            Get set up <ArrowRight className="w-4 h-4" />
          </button>
        </Card>
      ) : (
        <>
          <div className="grid sm:grid-cols-3 gap-4 mb-6" data-testid="race-summary-cards">
            <Card className="text-center">
              <div className="font-display text-4xl font-bold bg-gradient-to-r from-cyan-300 to-blue-400 bg-clip-text text-transparent">
                {cc.days_until != null ? cc.days_until : "—"}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">Days remaining</div>
            </Card>
            <Card className="text-center">
              <div className="font-display text-lg font-bold mt-2">{cc.election_date ? String(cc.election_date).slice(0, 10) : "Not set"}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">Election date</div>
            </Card>
            <Card className="text-center">
              <div className="font-display text-lg font-bold mt-2">{race?.name || [entity.office, entity.state].filter(Boolean).join(" · ") || "No race attached"}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">Race</div>
            </Card>
          </div>

          <Card className="mb-6" data-testid="race-candidates-card">
            <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
              <Users2 className="w-4 h-4 text-cyan-300" /> Candidates in this race
            </h3>
            {candidates.length === 0 ? (
              <p className="text-sm text-slate-500">
                {race ? "No public candidate roster available for this race yet." : "Attach your race during onboarding to load the public candidate roster."}
              </p>
            ) : (
              <div className="space-y-2">
                {candidates.slice(0, 8).map((c, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-white/5 bg-white/[0.02]" data-testid={`race-candidate-${i}`}>
                    <div className="min-w-0">
                      <div className="text-sm text-white truncate flex items-center gap-2">
                        {c.name}
                        {c.is_yours && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-400/20 border border-cyan-400/30 text-cyan-300 uppercase font-bold">Your candidate</span>}
                      </div>
                      <div className="text-[11px] text-slate-500">{[c.party, c.incumbent_challenge].filter(Boolean).join(" · ")}</div>
                    </div>
                    {c.receipts != null && (
                      <div className="text-right shrink-0">
                        <div className="text-sm font-semibold">${Number(c.receipts).toLocaleString()}</div>
                        <div className="text-[10px] text-slate-500">raised (public)</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>

          <div className="flex flex-wrap items-center gap-4">
            <button onClick={() => navigate("/race-intelligence")} data-testid="race-open-full-intelligence"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 font-semibold rounded-full px-5 py-2.5 text-sm hover:opacity-90">
              <Landmark className="w-4 h-4" /> Open Full Race Intelligence
            </button>
            <button onClick={() => navigate("/political-intelligence")} data-testid="race-open-pi"
              className="text-xs text-slate-500 hover:text-cyan-300 inline-flex items-center gap-1.5 transition-colors">
              <ShieldCheck className="w-3.5 h-3.5" /> Polling, issues & opposition tracking
            </button>
          </div>
        </>
      )}
    </div>
  );
}
